import PDFName from './PDFName.js';
import PDFNull from './PDFNull.js';
import PDFObject from './PDFObject.js';
import CharCodes from '../syntax/CharCodes.js';
import { isPDFInstance, PDFClasses } from '../../api/objects.js';
class PDFDict extends PDFObject {
    myClass() {
        return PDFClasses.PDFDict;
    }
    constructor(map, context) {
        super();
        this.suppressEncryption = false;
        this.dict = new Map(Array.from(map.entries()).map((entry) => [entry[0].toString(), entry]));
        this.context = context;
    }
    keys() {
        return Array.from(this.dict.values()).map((value) => value[0]);
    }
    values() {
        return Array.from(this.dict.values()).map((value) => value[1]);
    }
    entries() {
        return Array.from(this.dict.values());
    }
    set(key, value) {
        this.registerChange();
        this.dict.set(key.asString(), [key, value]);
    }
    get(key, 
    // TODO: `preservePDFNull` is for backwards compatibility. Should be
    // removed in next breaking API change.
    preservePDFNull = false) {
        if (!key.asString)
            return undefined;
        const value = this.dict.get(key.asString());
        if (!value ||
            (isPDFInstance(value[1], PDFClasses.PDFNull) && !preservePDFNull))
            return undefined;
        return value[1];
    }
    has(key) {
        if (!key.asString)
            return false;
        const value = this.dict.get(key.asString());
        return value !== undefined && !isPDFInstance(value[1], PDFClasses.PDFNull);
    }
    lookupMaybe(key, ...types) {
        // TODO: `preservePDFNull` is for backwards compatibility. Should be
        // removed in next breaking API change.
        const preservePDFNull = types.includes(PDFNull);
        const value = this.context.lookupMaybe(this.get(key, preservePDFNull), 
        // @ts-ignore
        ...types);
        if (isPDFInstance(value, PDFClasses.PDFNull) && !preservePDFNull)
            return undefined;
        return value;
    }
    lookup(key, ...types) {
        // TODO: `preservePDFNull` is for backwards compatibility. Should be
        // removed in next breaking API change.
        const preservePDFNull = types.includes(PDFNull);
        const value = this.context.lookup(this.get(key, preservePDFNull), 
        // @ts-ignore
        ...types);
        if (isPDFInstance(value, PDFClasses.PDFNull) && !preservePDFNull)
            return undefined;
        return value;
    }
    delete(key) {
        this.registerChange();
        return this.dict.delete(key.asString());
    }
    asMap() {
        return new Map(this.dict.values());
    }
    /** Generate a random key that doesn't exist in current key set */
    uniqueKey(tag = '') {
        const existingKeys = this.keys();
        let key = PDFName.of(this.context.addRandomSuffix(tag, 10));
        while (existingKeys.includes(key)) {
            key = PDFName.of(this.context.addRandomSuffix(tag, 10));
        }
        return key;
    }
    clone(context) {
        const clone = PDFDict.withContext(context || this.context);
        const entries = this.entries();
        for (let idx = 0, len = entries.length; idx < len; idx++) {
            const [key, value] = entries[idx];
            clone.set(key, value);
        }
        return clone;
    }
    toString() {
        let dictString = '<<\n';
        const entries = this.entries();
        for (let idx = 0, len = entries.length; idx < len; idx++) {
            const [key, value] = entries[idx];
            dictString += key.toString() + ' ' + value.toString() + '\n';
        }
        dictString += '>>';
        return dictString;
    }
    sizeInBytes() {
        let size = 5;
        const entries = this.entries();
        for (let idx = 0, len = entries.length; idx < len; idx++) {
            const [key, value] = entries[idx];
            size += key.sizeInBytes() + value.sizeInBytes() + 2;
        }
        return size;
    }
    copyBytesInto(buffer, offset) {
        const initialOffset = offset;
        buffer[offset++] = CharCodes.LessThan;
        buffer[offset++] = CharCodes.LessThan;
        buffer[offset++] = CharCodes.Newline;
        const entries = this.entries();
        for (let idx = 0, len = entries.length; idx < len; idx++) {
            const [key, value] = entries[idx];
            offset += key.copyBytesInto(buffer, offset);
            buffer[offset++] = CharCodes.Space;
            offset += value.copyBytesInto(buffer, offset);
            buffer[offset++] = CharCodes.Newline;
        }
        buffer[offset++] = CharCodes.GreaterThan;
        buffer[offset++] = CharCodes.GreaterThan;
        return offset - initialOffset;
    }
    registerChange() {
        this.context.registerObjectChange(this);
    }
}
PDFDict.className = () => PDFClasses.PDFDict;
PDFDict.withContext = (context) => new PDFDict(new Map(), context);
PDFDict.fromMapWithContext = (map, context) => new PDFDict(map, context);
export default PDFDict;
//# sourceMappingURL=PDFDict.js.map